import { coreBase, coreBaseV2, cxBase, cxBaseV3 } from '@/config/api';
import { BuildingCreateType, BuildingUpdateType } from '@/interface/buildingCreator';
import { SpinnerArea } from '@/interface/common';
import { PanelDetailType, PanelHistoryItemType } from '@/interface/panelTypes';
import request from '@/utils/request';

type CommissionPanelType = {
  id?: string;
  'activation-code': string;
  buildingId: string;
  earthGroundedNeutral: boolean;
  notes: string;
  uid: string;
};

type HistoryResponse = { history: PanelHistoryItemType[]; current: number; pages: number };

export type BuildingHealth = {
  currentPage: number;
  pages: number;
  total: number;
  dataGateways: GatewayHealth[];
};

export enum Confidence {
  Gray = 0,
  Green = 1,
  Red = -1,
}

export type GatewayHealth = {
  serialNumber: string;
  dataQuality: DataQuality;
  lastDataReceivedAt: string;
  isConnected: boolean;
  lastConnectedAt: string;
  status: Status;
};

type DataQuality = {
  metricWindow: {
    start: string; // Start time of Data API
    end: string; // End time of Data API
  };
  receivedData: {
    received: number;
    total: number;
  };
  sensors: [{ channel: number; confidence: Confidence }];
};

type Status = {
  connectivity: {
    connectivityType: string;
    connectivityStrength: number | null;
    connectivityUptime: string;
    ethernetState: string;
    ethernetAddress: string;
    publicAddress: string;
    ipAddress: string;
    softap0Mac: string;
    eth0Mac: string;
    wlan0Mac: string;
    defaultDNS: string;
  };
  sensors: {
    psocFirmwareVersion: string;
    voltageConfiguration: string;
    voltageCalibration: string;
    voltageLevel: string;
    voltageFrequency: string;
    chain1: {
      cable: string;
      hcimPorts: string;
      hcimUnoccupied: number[] | null;
      sensorCount: number;
    };
    chain2: {
      cable: string;
      hcimPorts: string;
      hcimUnoccupied: number[] | null;
      sensorCount: number;
    };
  };
  sdCard: {
    card: string;
    storageUsage: string;
    gdaqCount: string;
    snapshotCount: string;
    waveformCount: string;
    minutelyCount: string;
    minutelyV2Count: string;
  };
  services: {
    lastDataReceivedAt: string;
    initStatus: string;
    lastReboot: string;
    gdaqRunTime: string;
    gdaqconfRunTime: string;
    energonRunTime: string;
    droneRunTime: string;
    bpmRunTime: string;
    numReboots24h: string;
    lastRebootReason: string;
  };
  other: {
    cpu_load: string;
    lte_ip_address: string;
    monthTotal: string;
    todayTotal: string;
    monthUpload: string;
    todayUpload: string;
    emmcCapacity: string;
    phaseMapping: string;
    monthDownload: string;
    sdInodeUsage: string;
    todayDownload: string;
    monthEstimated: string;
    todayEstimated: string;
    emmcInodeUsage: string;
    minutelyV2Count: string;
    voltageDetailsPhase: string;
    voltageDetailsVoltage: string;
  };
};

export function getPanelInfo(panelId: number) {
  return request(`${cxBase}/panels/${panelId}`, { spinnerType: SpinnerArea.Panel });
}

export function commissionPanel(data: CommissionPanelType) {
  return request({
    url: data.id ? `${cxBase}/panels/${data.id}` : `${cxBase}/panels`,
    method: 'post',
    data,
    spinnerType: SpinnerArea.Panel,
  });
}

export function updatePanelInfo(panelId: number, panelDetail: PanelDetailType) {
  return request({
    url: `${cxBase}/panels/${panelId}`,
    method: 'put',
    data: panelDetail,
    spinnerType: SpinnerArea.Building,
  });
}

export function deletePanelInfo(panelId: number) {
  return request({
    url: `${cxBase}/panels/${panelId}`,
    method: 'delete',
    spinnerType: SpinnerArea.Building,
  });
}

export function getBuildings(params?: { filter?: { accounts?: { id: string } } }): any {
  return request({
    url: `${coreBaseV2}/buildings`,
    params,
    spinnerType: SpinnerArea.None,
  });
}

export function getBuilding(id: string): any {
  return request({
    url: `${coreBaseV2}/buildings/${id}`,
    params: {
      include: 'panels.breakers',
    },
    spinnerType: SpinnerArea.Building,
  });
}

export function createBuilding(data: BuildingCreateType) {
  return request({
    url: `${coreBaseV2}/buildings`,
    method: 'post',
    data,
  });
}

export function updateBuilding(data: BuildingUpdateType) {
  return request({
    url: `${coreBaseV2}/buildings/${data.data.id}`,
    method: 'patch',
    data,
  });
}

export function getEndUse(ids: string[]) {
  return request({
    url: `${coreBase}/end-uses?filter[id]=${ids.join(',')}`,
    spinnerType: SpinnerArea.Panel,
  });
}

export function getPanelHistory(query: {
  buildingId: string;
  panelId: number;
  perPage: number;
  page: number;
  showPanelLoading?: boolean;
}): Promise<HistoryResponse> {
  return request({
    url: `${cxBase}/buildings/${query.buildingId}/panels/${query.panelId}/history`,
    params: {
      'per-page': query.perPage || 10,
      page: query.page || 1,
    },
    method: 'get',
    spinnerType: query.showPanelLoading ? SpinnerArea.FullPanel : SpinnerArea.Dropdown,
  });
}

export function getBuildingCxPanels(
  buildingId: string,
  page = 1,
  perPage = 10,
  disableAbort?: boolean,
  showPanelLoading?: boolean
): Promise<{
  current: number;
  pages: number;
  panels: PanelDetailType[];
}> {
  return request({
    url: `${cxBase}/buildings/${buildingId}/panels`,
    params: {
      'per-page': perPage,
      page,
    },
    spinnerType: showPanelLoading ? SpinnerArea.FullPanel : SpinnerArea.None,
    abortToken: disableAbort ? undefined : buildingId,
  });
}

export function getDataGateways(
  uids: string[],
  page = 1,
  perPage = 10,
  params?: { start_time: string; end_time: string }
): Promise<BuildingHealth> {
  return request({
    url: `${cxBaseV3}/data-gateways`,
    method: 'get',
    params: {
      'per-page': perPage,
      page,
      include: 'data-quality,status',
      filter: {
        key: 'serialNumber',
        operator: 'in',
        value: uids,
        type: 'string',
      },
      ...params,
    },
    abortToken: uids.join(','),
    spinnerType: SpinnerArea.None,
  });
}
